import classNames from 'classnames'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'

import { Button } from 'core-components/library/button'
import { Flex } from 'core-components/library/flex'
import {
  Controller,
  isEmpty,
  ValidationStatus,
  Validator,
} from 'core-components/library/validation'

import { PropertyTooltip } from './components/PropertyTooltip'
import { RenderEditor } from './components/RenderEditor'
import { IExtendedProperties } from './models/IExtendedProperties'
import { IProperties } from './models/IProperties'
import { isPathString } from './validators/isPathString'

import styles from './PropertyGenerator.module.scss'

interface IPropertyGenerator {
  properties: IProperties[]
  onSave?: (properties: IProperties[]) => void
  autoSave?: boolean
}

export const PropertyGenerator = ({ properties, onSave, autoSave }: IPropertyGenerator) => {
  const [data, setData] = useState<IExtendedProperties[]>(() => [...properties])
  const [isValid, setIsValid] = useState<boolean>(true)
  const autoSaveTimerRef = useRef<number>(-1)

  const save = useCallback(() => {
    onSave && onSave(data)
  }, [onSave, data])

  const getPropertiesValueFromObjectDescription = useCallback(
    (objectDescription: IProperties[] = []) => {
      const newValue = {}
      objectDescription.map((el) => {
        newValue[el.name] = el.value
      })
      return newValue
    },
    []
  )

  const updateData = useCallback(
    (updateData?: IExtendedProperties[]) => {
      const newData = updateData || [...data]

      newData.map((item) => {
        if (item.type === 'object') {
          item.value = getPropertiesValueFromObjectDescription(item.objectDescription)
        }
        return item
      })
      setData(() => [...newData])
    },
    [data, getPropertiesValueFromObjectDescription]
  )

  const renderObjectFields = (property: IExtendedProperties) => {
    return property.objectDescription?.map((objectProperty, objectIndex) => {
      const objectData = property.objectDescription as IExtendedProperties[]
      if (!objectData[objectIndex].isObjectProperty) {
        objectData[objectIndex].isObjectProperty = true
      }
      return editorSwitcher(objectIndex, objectProperty, property.objectDescription!)
    })
  }

  const getCheckFunction = (props: IExtendedProperties[], property: IProperties, index: number) => {
    const isObjectProperty = props[index].isObjectProperty
    const isCustomEditor = props[index].customEditor
    if (isObjectProperty || isCustomEditor) {
      return null
    }

    if (!property.needLoad && !property.required) {
      return null
    }

    return async (checkValue: string) => {
      const isObjectProperty = props[index].isObjectProperty
      const isCustomEditor = props[index].customEditor
      if (isObjectProperty || isCustomEditor) {
        return ''
      }

      if (property.needLoad) {
        return isPathString(checkValue, {
          required: property.required,
          needLoad: property.needLoad,
        })
      }
      if (property.required) {
        return isEmpty(checkValue || '')
      }
      return ''
    }
  }

  useEffect(() => {
    if (autoSave) {
      clearTimeout(autoSaveTimerRef.current)
      autoSaveTimerRef.current = window.setTimeout(() => {
        save()
      }, 300)
    }
  }, [save, autoSave])

  useEffect(() => {
    updateData(properties)
  }, [properties])

  const editorSwitcher = (index: number, property: IProperties, props: IExtendedProperties[]) => {
    return (
      <Fragment key={`fragment-${property.name}`}>
        <div className={styles.cell}>
          <Flex gap={'xs'} justifyContent={'space-between'} className={styles.property}>
            <span>
              <span className={styles.property_name}>{property.name}</span>
              <span className={styles.property_type}>:{property.typeAlias || property.type}</span>
            </span>
            {property.tooltip ? <PropertyTooltip tooltip={property.tooltip} /> : null}
          </Flex>
        </div>
        <Validator
          key={`validator-${property.name}`}
          field={property.name}
          classes={{
            root: classNames(
              styles.cell,
              {
                [styles.invalidRow]: props[index].isValid === false,
              },
            ),
          }}
          value={property.value as string}
          onChangeValidation={(status) => {
            if (status !== ValidationStatus.WIP) {
              const newStatus = status === ValidationStatus.Valid
              if (newStatus !== data[index].isValid) {
                data[index].isValid = newStatus
                updateData()
              }
            }
          }}
          checkFn={getCheckFunction(props, property, index)}
        >
          <RenderEditor
            key={property.name}
            index={index}
            property={property}
            properties={props}
            updateData={updateData}
            renderObjectFields={renderObjectFields}
          />
        </Validator>
      </Fragment>
    )
  }

  return (
    <Controller
      onValidationStatusChange={({ status }) => {
        setIsValid(status === ValidationStatus.Valid)
      }}
    >
      <>
        <div className={styles.grid}>
          {data.map((property, index) => {
            return editorSwitcher(index, property, data)
          })}
        </div>

        {!autoSave ? (
          <div className={styles.saveButtonWrapper}>
            <Button disabled={!isValid} className={styles.saveButton} onClick={save}>
              Generate
            </Button>
          </div>
        ) : null}
      </>
    </Controller>
  )
}
